import React, { useState } from 'react';
import Logo1 from './Logo1.png';
import Logo2 from './Logo2.png';
import { Analytics } from "@vercel/analytics/react";


const GeneText = ({ children }) => {
  if (!children) return null;
  
  const genes = ['RUNX1::RUNX1T1', 'CBFB::MYH11', 'FLT3', 'NPM1', 'PML::RARA', 'ABL1', 'CEBPA', 'KMT2A'];
  
  const processText = (text) => {
    if (typeof text !== 'string') return text;
    
    let parts = [text];
    genes.forEach(gene => {
      parts = parts.flatMap(part => {
        if (typeof part !== 'string') return part;
        return part.split(new RegExp(`(${gene.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'))
          .map((segment, i) => {
            if (i % 2 === 1) return <i key={`${gene}-${i}`}>{segment}</i>;
            return segment;
          })
          .filter(s => s !== '');
      });
    });
    
    return parts;
  };
  
  return <>{processText(children)}</>;
};

function App() {
  const [amlType, setAmlType] = useState('');
  const [subtype, setSubtype] = useState('');
  const [timePoint, setTimePoint] = useState('');
  const [selectedTable, setSelectedTable] = useState(null);
  const [introExpanded, setIntroExpanded] = useState(false);

  // Define risk categories
  const subtypeOptions = {
    favorable: [
      { value: 'NPM1mut_wo_FLT3ITD', label: 'mutNPM1 without FLT3-ITD' },
      { value: 'CBF_AML', label: 'RUNX1::RUNX1T1 and CBFB::MYH11' },
      { value: 'PML_RARA', label: 'PML::RARA' },
      { value: 'CEBPA_bZIP', label: 'CEBPA bZIP in-frame' }
    ],
    intermediate: [
      { value: 'FLT3ITD_NPM1wt', label: 'FLT3-ITD and NPM1wt' },
      { value: 'FLT3ITD_NPM1mut', label: 'FLT3-ITD and mutNPM1' },
      { value: 'KMT2A_MLLT3', label: 'KMT2A::MLLT3, other fusion genes¹' },
      { value: 'Other_int', label: 'Other intermediate risk' }
    ],
    adverse: [
      { value: 'Fusion_KMT2A', label: 'Fusion genes (e.g., KMT2A::X)' },
      { value: 'Other_adv', label: 'Other adverse risk' }
    ]
  };

  // Time points for each subtype
  const timePointOptions = {
    // Common time points
    baseline: { label: 'Baseline' },
    induction_PML: { label: 'After induction / during consolidation' },
    cycles_2: { label: 'After 2 cycles of intensive chemotherapy / Pre-alloHCT' },
    eot: { label: 'End of treatment (after consolidation / alloHCT)' },
    eot_PML: { label: 'End of treatment (after consolidation)' },
    eot_intOther: { label: 'End of treatment after alloHCT' },
    follow_up: { label: 'Follow-up' },
    pre_hct: { label: 'Pre-alloHCT' }
  };

  // Specific time points per subtype
  const subtypeTimePoints = {
    NPM1mut_wo_FLT3ITD: ['baseline', 'cycles_2', 'eot', 'follow_up'],
    CBF_AML: ['baseline', 'cycles_2', 'eot', 'follow_up'],
    PML_RARA: ['baseline', 'induction_PML', 'eot_PML', 'follow_up'],
    CEBPA_bZIP: ['baseline', 'cycles_2', 'eot', 'follow_up'],
    FLT3ITD_NPM1wt: ['baseline', 'cycles_2', 'eot', 'follow_up'],
    FLT3ITD_NPM1mut: ['baseline', 'cycles_2', 'eot', 'follow_up'],
    KMT2A_MLLT3: ['baseline', 'cycles_2', 'eot', 'follow_up'],
    Fusion_KMT2A: ['baseline', 'cycles_2', 'eot_intOther', 'follow_up'],
    Other_int: ['baseline', 'cycles_2', 'eot_intOther', 'follow_up'],
    Other_adv: ['baseline', 'cycles_2', 'eot_intOther', 'follow_up']
  };

  // Map subtype to monitoring table
  const subtypeToTable = {
    NPM1mut_wo_FLT3ITD: 'NPM1',
    CBF_AML: 'CBF',
    PML_RARA: 'PML_RARA',
    CEBPA_bZIP: 'MFC',
    FLT3ITD_NPM1wt: ['FLT3_ITD_NGS', 'MFC'],
    FLT3ITD_NPM1mut: ['NPM1', 'FLT3_ITD_NGS'],
    KMT2A_MLLT3: 'MFC',
    Fusion_KMT2A: 'MFC',
    Other_int: ['MFC', 'Int_UHSNGS'],
    Other_adv: ['MFC', 'Adv_UHSNGS']
  };

  // Tables definitions (keeping all existing table data)
  const tables = {
    CBF: {
      title: "Monitoring by RUNX1::RUNX1T1 qPCR or CBFB::MYH11 qPCR (CBF AML)",
      timePoints: {
        baseline: {
          assay: "qPCR",
          tissue: "Bone marrow (BM)¹⁹",
          rows: [
            {
              threshold: "Quantify baseline transcript levels for log reduction",
              definition: "-",
              response: "-"
            }
          ]
        },
        cycles_2: {
          assay: "qPCR",
          tissue: "Bone marrow (BM)",
          rows: [
            {
              threshold: "≥3-log₁₀ reduction from diagnostic levels",
              definition: "Low-level positive/negative",
              response: "Optimal"
            },
            {
              threshold: "<3-log₁₀ reduction from diagnostic levels",
              definition: "Positive",
              response: "Warning"
            }
          ]
        },
        eot: {
          assay: "qPCR",
          tissue: "Bone marrow (BM)",
          rows: [
            {
              threshold: "<0.001% OR undetectable¹",
              definition: "Negative",
              response: "Optimal"
            },
            {
              threshold: "≥3-log₁₀ reduction from diagnostic levels",
              definition: "Low-level positive",
              response: "Warning³"
            },
            {
              threshold: "<3-log₁₀ reduction from diagnostic levels",
              definition: "Positive",
              response: "High risk of treatment failure"
            }
          ]
        },
        follow_up: {
          assay: "qPCR",
          tissue: "Peripheral blood (PB) or bone marrow (BM)",
          FU_duration: "Due to rapid relapses during the first 12 months monitoring from PB every 4-6 weeks is preferred.",
          rows: [
            {
              threshold: "<0.001% OR undetectable¹",
              definition: "Negative",
              response: "Optimal"
            },
            {
              threshold: "Detectable² AND ≥3-log₁₀ reduction from diagnostic levels OR detectable² AND <0.1%",
              definition: "Low-level positive",
              response: "Warning³"
            },
            {
              threshold: "Conversion from undetectable¹ to detectable² AND ≥0.1%⁴,⁶",
              definition: "MRD relapse",
              response: "MRD relapse"
            }
          ]
        }
      },
      footnotes: {
        "1": "Undetectable by qPCR: CT≥40 in ≥2/3 replicates and ≥10,000 ABL1 copies, optimally ≥30,000 ABL1 copies.",
        "2": "Detectable by qPCR: CT<40 in ≥2/3 replicates.",
        "3": "MRD response in the warning category during follow-up should be monitored more closely with a repeat MRD assessment recommended after approximately four weeks.",
        "4": "In patients who have never tested MRD-negative, a ≥1 log₁₀ increase from the measured nadir within the same tissue used for the nadir measurement regardless of threshold is required.",
        "6": "MRD relapse based on a qPCR test should be promptly confirmed (≤4 weeks) by a second consecutive sample, preferably both in PB and BM.",
        "19": "The analyte's baseline level must be established in bone marrow at time of diagnosis to enable comparison with MRD levels after two cycles and at end of treatment."
      }
    },
    NPM1: {
      title: "Monitoring by NPM1 qPCR using cDNA",
      timePoints: {
        baseline: {
          assay: "qPCR",
          tissue: "Peripheral blood (if blasts ≥20%) or bone marrow (BM)¹⁸",
          rows: [
            {
              threshold: "Usually not applicable¹⁸",
              definition: "-",
              response: "-"
            }
          ]
        },
        cycles_2: {
          assay: "qPCR",
          tissue: "Peripheral blood (PB)",
          rows: [
            {
              threshold: "<0.001% OR undetectable¹",
              definition: "Negative",
              response: "Optimal"
            },
            {
              threshold: "≥0.001% to <0.01% AND detectable²",
              definition: "Low-level positive",
              response: "Warning"
            },
            {
              threshold: "≥0.01% AND detectable²",
              definition: "Positive",
              response: "Warning"
            }
          ]
        },
        eot: {
          assay: "qPCR",
          tissue: "Bone marrow (BM)",
          rows: [
            {
              threshold: "<0.001% OR undetectable¹",
              definition: "Negative",
              response: "Optimal"
            },
            {
              threshold: "≥0.001% to <0.1% AND detectable²",
              definition: "Low-level positive",
              response: "Warning"
            },
            {
              threshold: "≥0.1% AND detectable²",
              definition: "Positive",
              response: "High risk of treatment failure"
            }
          ]
        },
        follow_up: {
          assay: "qPCR",
          tissue: "Peripheral blood (PB) or bone marrow (BM)",
          FU_duration: "In PB every 4-6 weeks for 24 months. In BM every 3 months for at least 24 months if not indicated otherwise",
          rows: [
            {
              tissue: "PB",
              threshold: "<0.001% OR undetectable¹",
              definition: "Negative",
              response: "Optimal"
            },
            {
              tissue: "PB",
              threshold: "≥0.001% to <0.01% AND detectable²",
              definition: "Low-level positive",
              response: "Warning³"
            },
            {
              tissue: "PB",
              threshold: "≥0.01% AND detectable²,⁴,⁵",
              definition: "MRD relapse",
              response: "MRD relapse"
            },
            {
              tissue: "BM",
              threshold: "<0.001% OR undetectable¹",
              definition: "Negative",
              response: "Optimal"
            },
            {
              tissue: "BM",
              threshold: "≥0.001% to <0.1% AND detectable²",
              definition: "Low-level positive",
              response: "Warning³"
            },
            {
              tissue: "BM",
              threshold: "≥0.1% AND detectable²,⁴,⁵",
              definition: "MRD relapse",
              response: "MRD relapse"
            }
          ]
        }
      },
      footnotes: {
        "1": "Undetectable by qPCR: CT≥40 in ≥2/3 replicates and ≥10,000 ABL1 copies, optimally ≥30,000 ABL1 copies.",
        "2": "Detectable by qPCR: CT<40 in ≥2/3 replicates.",
        "3": "MRD response in the warning category during follow-up should be monitored more closely with a repeat MRD assessment recommended after approximately four weeks.",
        "4": "In patients who have never tested MRD-negative, a ≥1 log₁₀ increase from the measured nadir within the same tissue used for the nadir measurement regardless of threshold is required.",
        "5": "MRD relapse based on a qPCR test should be promptly confirmed (≤4 weeks) by a second consecutive sample, preferably both in PB and BM, unless mutNPM1/ABL1 copies are ≥1% in BM.",
        "18": "If a rare MRD marker is to be used for monitoring (e.g. non--type A, B, or D NPM1 mutations, very large FLT3-ITD insertions, or uncommon non-canonical PML::RARA transcripts), analyte detectability should be confirmed at diagnosis using the established MRD assay to ensure its suitability for subsequent monitoring."
      }
    },
    PML_RARA: {
      title: "Monitoring by PML::RARA qPCR (APL)",
      timePoints: {
        baseline: {
          assay: "qPCR",
          tissue: "Peripheral blood (if blasts ≥20%) or bone marrow (BM)¹⁸",
          rows: [
            {
              threshold: "Usually not applicable¹⁸",
              definition: "-",
              response: "-"
            }
          ]
        },
        induction_PML: {
          assay: "Not recommended",
          tissue: "Not applicable",
          rows: [
            {
              threshold: "-",
              definition: "-",
              response: "-"
            }
          ]
        },
        eot_PML: {
          assay: "qPCR",
          tissue: "Bone marrow (BM)",
          rows: [
            {
              threshold: "<0.001%⁷ OR undetectable¹",
              definition: "Negative",
              response: "Optimal"
            },
            {
              threshold: "≥0.001%⁷ AND detectable²",
              definition: "MRD positive",
              response: "Warning³"
            }
          ]
        },
        follow_up: {
          assay: "qPCR: MRD monitoring during follow-up recommended only if MRD-positive at EOT, and in chemotherapy-treated high-risk APL even when MRD-negative¹⁵",
          tissue: "Bone marrow (BM)¹⁶",
          FU_duration: "In PB every 4-6 weeks for 24 months. In BM every 3 months for at least 24 months if not indicated otherwise",
          rows: [
            {
              threshold: "<0.001% OR undetectable¹",
              definition: "Negative",
              response: "Optimal"
            },
            {
              threshold: "≥0.001% AND detectable²,⁴,⁶",
              definition: "MRD relapse",
              response: "MRD relapse"
            }
          ]
        }
      },
      footnotes: {
        "1": "Undetectable by qPCR: CT≥40 in ≥2/3 replicates and ≥10,000 ABL1 copies, optimally ≥30,000 ABL1 copies.",
        "2": "Detectable by qPCR: CT<40 in ≥2/3 replicates.",
        "3": "MRD response in the warning category during follow-up should be monitored more closely with a repeat MRD assessment recommended after approximately four weeks.",
        "4": "In patients who have never tested MRD-negative, a ≥1 log₁₀ increase from the measured nadir within the same tissue used for the nadir measurement regardless of threshold is required.",
        "6": "MRD relapse based on a qPCR test should be promptly confirmed (≤4 weeks) by a second consecutive sample, preferably both in PB and BM.",
        "7": "The transcript abundance is typically lower for PML::RARA fusion transcripts compared to other common fusion genes, thus a sensitivity of 0.001% may not be achieved in all patients",
        "15": "MRD monitoring during follow-up recommended only if MRD-positive at EOT, and in chemotherapy-treated high-risk APL even when MRD-negative",
        "16": "PB is not recommended for monitoring as PB usually becomes MRD positive only at frank APL relapse.",
        "18": "If a rare MRD marker is to be used for monitoring (e.g. non--type A, B, or D NPM1 mutations, very large FLT3-ITD insertions, or uncommon non-canonical PML::RARA transcripts), analyte detectability should be confirmed at diagnosis using the established MRD assay to ensure its suitability for subsequent monitoring."
      }
    },
    FLT3_ITD_NGS: {
      title: "Monitoring by targeted FLT3-ITD UHS-NGS",
      timePoints: {
        baseline: {
          assay: "UHS-NGS",
          tissue: "Peripheral blood (if blasts ≥20%) or bone marrow (BM)¹⁸",
          rows: [
            {
              threshold: "Usually not applicable¹⁸",
              definition: "-",
              response: "-"
            }
          ]
        },
        cycles_2: {
          assay: "FLT3-ITD UHS-NGS",
          tissue: "Bone marrow (BM) preferred over peripheral blood (PB)",
          rows: [
            {
              threshold: "<LOD⁹",
              definition: "Negative",
              response: "Optimal"
            },
            {
              threshold: "≥LOD⁹",
              definition: "Positive",
              response: "High risk of treatment failure"
            }
          ]
        },
        eot: {
          assay: "FLT3-ITD UHS-NGS: Limited evidence and based on expert opinion.",
          tissue: "Bone marrow (BM) preferred over peripheral blood (PB)",
          rows: [
            {
              threshold: "<LOD⁹",
              definition: "Negative",
              response: "Optimal"
            },
            {
              threshold: "≥LOD⁹",
              definition: "Positive",
              response: "High risk of treatment failure"
            }
          ]
        },
        follow_up: {
          assay: "FLT3-ITD UHS-NGS: Limited evidence and based on expert opinion.",
          tissue: "Peripheral blood (PB) or bone marrow (BM)",
          FU_duration: "In PB every 4-6 weeks for 24 months. In BM every 3 months for at least 24 months if not indicated otherwise",
          rows: [
            {
              threshold: "<LOD⁹",
              definition: "Negative",
              response: "Optimal"
            },
            {
              threshold: "≥LOD⁹,¹⁰",
              definition: "MRD relapse",
              response: "MRD relapse"
            }
          ]
        }
      },
      footnotes: {
        "9": "The assay should be validated to a limit of detection (LOD) of 10⁻⁴ to 10⁻⁵ (VAF).",
        "10": "MRD relapse as defined for FLT3-ITD may not require a repeat sample as it is associated with imminent relapse. However, low-level FLT3-ITD MRD ≥LOD but <0.01% VAF should be promptly confirmed in a repeat sample, particularly when the FLT3-ITD clone differs from that detected at diagnosis.",
        "18": "If a rare MRD marker is to be used for monitoring (e.g. non--type A, B, or D NPM1 mutations, very large FLT3-ITD insertions, or uncommon non-canonical PML::RARA transcripts), analyte detectability should be confirmed at diagnosis using the established MRD assay to ensure its suitability for subsequent monitoring."
      }
    },
    Adv_UHSNGS: {
      title: "Monitoring by UHS-NGS",
      timePoints: {
        baseline: {
          assay: "UHS-NGS²⁰",
          tissue: "Peripheral blood (if blasts ≥20%) or bone marrow (BM)",
          rows: [
            {
              threshold: "Assess for gene mutations at diagnosis, e.g. by myeloid panel analysis²¹",
              definition: "-",
              response: "-"
            }
          ]
        },
        cycles_2: {
          assay: "UHS-NGS²⁰",
          tissue: "Bone marrow (BM) or peripheral blood (PB)",
          rows: [
            {
              threshold: "<LOD⁹",
              definition: "Negative",
              response: "-"
            },
            {
              threshold: "≥LOD to <0.01%",
              definition: "Low-level positive",
              response: "-"
            },
            {
              threshold: "≥0.01%",
              definition: "Positive",
              response: "-"
            }
          ]
        },
        eot_intOther: {
          assay: "UHS-NGS²⁰",
          tissue: "Bone marrow (BM) preferred over peripheral blood (PB)",
          rows: [
            {
              threshold: "<LOD",
              definition: "Negative",
              response: "-"
            },
            {
              threshold: "≥LOD to <0.01%",
              definition: "Low-level positive",
              response: "-"
            },
            {
              threshold: "≥0.01%",
              definition: "Positive",
              response: "-"
            }
          ]
        },
        follow_up: {
          assay: "UHS-NGS²⁰: Limited evidence, based on expert opinion. Only for well-validated markers such as NPM1. Future incorporation of additional genes after rigorous validation using a standardized reporting framework is expected (Supplemental Table S7, shown below).",
          tissue: "Peripheral blood (PB) or bone marrow (BM)",
          FU_duration: "In PB every 4-6 weeks for 24 months. In BM every 3 months for at least 24 months if not indicated otherwise",
          rows: [
            {
              threshold: "<LOD⁹",
              definition: "Negative",
              response: "-"
            },
            {
              threshold: "≥LOD to <0.01%",
              definition: "Low-level positive",
              response: "-"
            },
            {
              threshold: "≥0.01%¹⁷. In patients who have never tested MRD-negative, a ≥1 log₁₀ increase from the measured nadir within the same tissue used for the nadir measurement is required",
              definition: "MRD relapse",
              response: "-"
            }
          ]
        }
      },
      footnotes: {
        "9": "The assay should be validated to a limit of detection (LOD) of 10⁻⁴ to 10⁻⁵ (VAF).",
        "17": "Provisional definition, should be promptly confirmed (≤4 weeks) by a second consecutive sample.",
        "18": "If a rare MRD marker is to be used for monitoring (e.g. non--type A, B, or D NPM1 mutations, very large FLT3-ITD insertions, or uncommon non-canonical PML::RARA transcripts), analyte detectability should be confirmed at diagnosis using the established MRD assay to ensure its suitability for subsequent monitoring.",
        "20": "If mutNPM1 or FLT3-ITD is present in patients with ELN adverse risk, refer to recommendations for mutNPM1 and FLT3-ITD positive patients.",
        "21": "Assess for gene mutations at diagnosis, e.g. by myeloid panel analysis."
      }
    },
    Int_UHSNGS: {
      title: "Monitoring by UHS-NGS",
      timePoints: {
        baseline: {
          assay: "UHS-NGS²⁰",
          tissue: "Peripheral blood (if blasts ≥20%) or bone marrow (BM)",
          rows: [
            {
              threshold: "Assess for gene mutations at diagnosis, e.g. by myeloid panel analysis²¹",
              definition: "-",
              response: "-"
            }
          ]
        },
        eot_intOther: {
          assay: "UHS-NGS",
          tissue: "Bone marrow (BM) preferred over peripheral blood (PB)",
          rows: [
            {
              threshold: "<LOD",
              definition: "Negative",
              response: "-"
            },
            {
              threshold: "≥LOD to <0.01%",
              definition: "Low-level positive",
              response: "-"
            },
            {
              threshold: "≥0.01%",
              definition: "Positive",
              response: "-"
            }
          ]
        },
        follow_up: {
          assay: "UHS-NGS: Limited evidence, based on expert opinion. Only for well-validated markers such as NPM1. Future incorporation of additional genes after rigorous validation using a standardized reporting framework is expected (Supplemental Table S7, shown below).",
          tissue: "Peripheral blood (PB) or bone marrow (BM)",
          FU_duration: "In PB every 4-6 weeks for 24 months. In BM every 3 months for at least 24 months if not indicated otherwise",
          rows: [
            {
              threshold: "<LOD",
              definition: "Negative",
              response: "-"
            },
            {
              threshold: "≥LOD to <0.01%",
              definition: "Low-level positive",
              response: "-"
            },
            {
              threshold: "≥0.01%¹⁷. In patients who have never tested MRD-negative, a ≥1 log₁₀ increase from the measured nadir within the same tissue used for the nadir measurement is required",
              definition: "MRD relapse",
              response: "-"
            }
          ]
        }
      },
      footnotes: {
        "9": "The assay should be validated to a limit of detection (LOD) of 10⁻⁴ to 10⁻⁵ (VAF).",
        "17": "Provisional definition, should be promptly confirmed (≤4 weeks) by a second consecutive sample.",
        "18": "If a rare MRD marker is to be used for monitoring (e.g. non--type A, B, or D NPM1 mutations, very large FLT3-ITD insertions, or uncommon non-canonical PML::RARA transcripts), analyte detectability should be confirmed at diagnosis using the established MRD assay to ensure its suitability for subsequent monitoring.",
        "20": "If mutNPM1 or FLT3-ITD is present in patients with ELN adverse risk, refer to recommendations for mutNPM1 and FLT3-ITD positive patients.",
        "21": "Assess for gene mutations at diagnosis, e.g. by myeloid panel analysis."
      }
    },
    MFC: {
      title: "Monitoring by MFC",
      timePoints: {
        baseline: {
          assay: "MFC",
          tissue: "Bone marrow (BM) or peripheral blood (PB)¹¹",
          rows: [
            {
              threshold: "≥10% of blasts",
              definition: "LAIP assessment",
              response: "-"
            }
          ]
        },
        cycles_2: {
          assay: "MFC",
          tissue: "Bone marrow (BM)",
          rows: [
            {
              threshold: "<0.01%¹² OR <LOD¹²",
              definition: "Negative",
              response: "Optimal"
            },
            {
              threshold: "≥0.01% to <0.1% AND >LOD",
              definition: "Low-level positive",
              response: "Warning"
            },
            {
              threshold: "≥0.1%",
              definition: "Positive",
              response: "High risk of treatment failure"
            }
          ]
        },
        eot: {
          assay: "MFC",
          tissue: "Bone marrow (BM)",
          rows: [
            {
              threshold: "<0.01%¹² OR <LOD¹²",
              definition: "Negative",
              response: "Optimal"
            },
            {
              threshold: "≥0.01% to <0.1% AND >LOD",
              definition: "Low-level positive",
              response: "Warning"
            },
            {
              threshold: "≥0.1%",
              definition: "Positive",
              response: "High risk of treatment failure"
            }
          ]
        },
        eot_intOther: {
          assay: "MFC",
          tissue: "Bone marrow (BM)",
          rows: [
            {
              threshold: "<0.01%¹² OR <LOD¹²",
              definition: "Negative",
              response: "Optimal"
            },
            {
              threshold: "≥0.01% to <0.1% AND >LOD",
              definition: "Low-level positive",
              response: "Warning"
            },
            {
              threshold: "≥0.1%",
              definition: "Positive",
              response: "High risk of treatment failure"
            }
          ]
        },
        follow_up: {
          assay: "MFC",
          tissue: "Bone marrow (BM)",
          FU_duration: "In BM every 3 months for 12 months",
          rows: [
            {
              threshold: "<0.01%¹² or <LOD¹²",
              definition: "Negative",
              response: "Optimal"
            },
            {
              threshold: "≥0.01% to <0.1% AND >LOD",
              definition: "Low-level positive",
              response: "Warning³"
            },
            {
              threshold: "≥0.1%¹³,¹⁴",
              definition: "MRD relapse",
              response: "MRD relapse"
            }
          ]
        }
      },
      footnotes: {
        "3": "MRD response in the warning category during follow-up should be monitored more closely with a repeat MRD assessment recommended after approximately four weeks.",
        "11": "If blasts are present in PB",
        "12": "Requirements for a sample that qualifies to determine MRD negativity: ≥500,000 CD45-expressing cells assessed for the best aberrancy(s) available (see recommendation B10). This results in LOQ at 50 LAIP cells (0.01%) and LOD at 20 LAIP cells (0.004%).",
        "13": "MRD relapse as defined for MFC may not require a repeat sample in cases with high diagnostic certainty of LAIP or DfN.",
        "14": "Defined as MRD relapse after achieving prior MRD negative remission or remission with low level positive MRD."
      }
    }
  };

  const handleAmlTypeChange = (e) => {
    setAmlType(e.target.value);
    setSubtype('');
    setTimePoint('');
    setSelectedTable(null);
  };

  const handleSubtypeChange = (e) => {
    setSubtype(e.target.value);
    setTimePoint('');
    // Set the table when subtype is selected
    if (e.target.value && subtypeToTable[e.target.value]) {
      // Reset selected table
      setSelectedTable(null);
      // For array of tables, set to first table
      if (Array.isArray(subtypeToTable[e.target.value])) {
        const primaryTable = subtypeToTable[e.target.value][0];
        setSelectedTable(tables[primaryTable]);
      } else {
        // For single table
        setSelectedTable(tables[subtypeToTable[e.target.value]]);
      }
    } else {
      setSelectedTable(null);
    }
  };

  // Get rows for the selected time point
  const getTimePointData = () => {
    if (!selectedTable || !timePoint || !selectedTable.timePoints[timePoint]) {
      return null;
    }
    return selectedTable.timePoints[timePoint];
  };

  // Get referenced footnote numbers
  const getReferencedFootnotes = (rows, timePointData) => {
    const referencedNumbers = new Set();
    
    // Check assay and tissue fields for footnotes
    if (timePointData) {
      const assay = timePointData.assay || '';
      const tissue = timePointData.tissue || '';
      const response = timePointData.response || '';
      
      // Check assay for footnote references
      const assayMatches = assay.match(/[¹²³⁴⁵⁶⁷⁸⁹⁰]/g);
      if (assayMatches) {
        assayMatches.forEach(match => {
          const num = {'⁰':0, '¹':1, '²':2, '³':3, '⁴':4, '⁵':5, '⁶':6, '⁷':7, '⁸':8, '⁹':9}[match];
          if (num !== undefined) referencedNumbers.add(num.toString());
        });
      }
      
      // Check for two-digit superscripts in assay
      const assayTwoDigitMatches = assay.match(/[¹²³⁴⁵⁶⁷⁸⁹][⁰¹²³⁴⁵⁶⁷⁸⁹]/g);
      if (assayTwoDigitMatches) {
        assayTwoDigitMatches.forEach(match => {
          const firstDigit = {'¹':1, '²':2, '³':3, '⁴':4, '⁵':5, '⁶':6, '⁷':7, '⁸':8, '⁹':9}[match[0]];
          const secondDigit = {'⁰':0, '¹':1, '²':2, '³':3, '⁴':4, '⁵':5, '⁶':6, '⁷':7, '⁸':8, '⁹':9}[match[1]];
          if (firstDigit !== undefined && secondDigit !== undefined) {
            const num = firstDigit * 10 + secondDigit;
            referencedNumbers.add(num.toString());
          }
        });
      }
      
      // Check response for references
      const responseMatches = response.match(/[¹²³⁴⁵⁶⁷⁸⁹⁰]/g);
      if (responseMatches) {
        responseMatches.forEach(match => {
          const num = {'⁰':0, '¹':1, '²':2, '³':3, '⁴':4, '⁵':5, '⁶':6, '⁷':7, '⁸':8, '⁹':9}[match];
          if (num !== undefined) referencedNumbers.add(num.toString());
        });
      }
      
      // Check for two-digit superscripts in response
      const responseTwoDigitMatches = response.match(/[¹²³⁴⁵⁶⁷⁸⁹][⁰¹²³⁴⁵⁶⁷⁸⁹]/g);
      if (responseTwoDigitMatches) {
        responseTwoDigitMatches.forEach(match => {
          const firstDigit = {'¹':1, '²':2, '³':3, '⁴':4, '⁵':5, '⁶':6, '⁷':7, '⁸':8, '⁹':9}[match[0]];
          const secondDigit = {'⁰':0, '¹':1, '²':2, '³':3, '⁴':4, '⁵':5, '⁶':6, '⁷':7, '⁸':8, '⁹':9}[match[1]];
          if (firstDigit !== undefined && secondDigit !== undefined) {
            const num = firstDigit * 10 + secondDigit;
            referencedNumbers.add(num.toString());
          }
        });
      }
      
      // Check for two-digit superscripts in tissue FIRST
      let processedTissue = tissue;
      const tissueTwoDigitMatches = tissue.match(/[¹²³⁴⁵⁶⁷⁸⁹][⁰¹²³⁴⁵⁶⁷⁸⁹]/g);
      if (tissueTwoDigitMatches) {
        tissueTwoDigitMatches.forEach(match => {
          const firstDigit = {'¹':1, '²':2, '³':3, '⁴':4, '⁵':5, '⁶':6, '⁷':7, '⁸':8, '⁹':9}[match[0]];
          const secondDigit = {'⁰':0, '¹':1, '²':2, '³':3, '⁴':4, '⁵':5, '⁶':6, '⁷':7, '⁸':8, '⁹':9}[match[1]];
          if (firstDigit !== undefined && secondDigit !== undefined) {
            const num = firstDigit * 10 + secondDigit;
            referencedNumbers.add(num.toString());
            processedTissue = processedTissue.replace(match, '');
          }
        });
      }
      
      // Check for single-digit superscripts
      const tissueMatches = processedTissue.match(/[¹²³⁴⁵⁶⁷⁸⁹⁰]/g);
      if (tissueMatches) {
        tissueMatches.forEach(match => {
          const num = {'⁰':0, '¹':1, '²':2, '³':3, '⁴':4, '⁵':5, '⁶':6, '⁷':7, '⁸':8, '⁹':9}[match];
          if (num !== undefined) referencedNumbers.add(num.toString());
        });
      }
    }
    
    if (!rows) return Array.from(referencedNumbers).sort((a, b) => parseInt(a) - parseInt(b));
    
    // Check thresholds and responses for footnote references
    rows.forEach(row => {
      // Check in threshold
      const threshold = row.threshold || '';
      
      // Process threshold - two-digit FIRST
      let processedThreshold = threshold;
      const twoDigitMatches = threshold.match(/[¹²³⁴⁵⁶⁷⁸⁹][⁰¹²³⁴⁵⁶⁷⁸⁹]/g);
      if (twoDigitMatches) {
        twoDigitMatches.forEach(match => {
          const firstDigit = {'¹':1, '²':2, '³':3, '⁴':4, '⁵':5, '⁶':6, '⁷':7, '⁸':8, '⁹':9}[match[0]];
          const secondDigit = {'⁰':0, '¹':1, '²':2, '³':3, '⁴':4, '⁵':5, '⁶':6, '⁷':7, '⁸':8, '⁹':9}[match[1]];
          if (firstDigit !== undefined && secondDigit !== undefined) {
            const num = firstDigit * 10 + secondDigit;
            referencedNumbers.add(num.toString());
            processedThreshold = processedThreshold.replace(match, '');
          }
        });
      }
      
      // Then single-digit superscripts
      const thresholdMatches = processedThreshold.match(/[¹²³⁴⁵⁶⁷⁸⁹⁰]/g);
      if (thresholdMatches) {
        thresholdMatches.forEach(match => {
          const num = {'⁰':0, '¹':1, '²':2, '³':3, '⁴':4, '⁵':5, '⁶':6, '⁷':7, '⁸':8, '⁹':9}[match];
          if (num !== undefined) referencedNumbers.add(num.toString());
        });
      }
      
      // Check response field in each row
      const response = row.response || '';
      const responseMatches = response.match(/[¹²³⁴⁵⁶⁷⁸⁹⁰]/g);
      if (responseMatches) {
        responseMatches.forEach(match => {
          const num = {'⁰':0, '¹':1, '²':2, '³':3, '⁴':4, '⁵':5, '⁶':6, '⁷':7, '⁸':8, '⁹':9}[match];
          if (num !== undefined) referencedNumbers.add(num.toString());
        });
      }
      
      // Check for two-digit superscripts in response
      const responseTwoDigitMatches = response.match(/[¹²³⁴⁵⁶⁷⁸⁹][⁰¹²³⁴⁵⁶⁷⁸⁹]/g);
      if (responseTwoDigitMatches) {
        responseTwoDigitMatches.forEach(match => {
          const firstDigit = {'¹':1, '²':2, '³':3, '⁴':4, '⁵':5, '⁶':6, '⁷':7, '⁸':8, '⁹':9}[match[0]];
          const secondDigit = {'⁰':0, '¹':1, '²':2, '³':3, '⁴':4, '⁵':5, '⁶':6, '⁷':7, '⁸':8, '⁹':9}[match[1]];
          if (firstDigit !== undefined && secondDigit !== undefined) {
            const num = firstDigit * 10 + secondDigit;
            referencedNumbers.add(num.toString());
          }
        });
      }
      
      // Check definition for references too
      const definition = row.definition || '';
      const defMatches = definition.match(/[¹²³⁴⁵⁶⁷⁸⁹⁰]/g);
      if (defMatches) {
        defMatches.forEach(match => {
          const num = {'⁰':0, '¹':1, '²':2, '³':3, '⁴':4, '⁵':5, '⁶':6, '⁷':7, '⁸':8, '⁹':9}[match];
          if (num !== undefined) referencedNumbers.add(num.toString());
        });
      }
    });
    
    return Array.from(referencedNumbers).sort((a, b) => parseInt(a) - parseInt(b));
  };

  // Format time point label for display
  const getTimePointLabel = (tp) => {
    switch(tp) {
      case 'baseline': return 'Baseline';
      case 'induction_PML': return 'After induction / during consolidation';
      case 'cycles_2': return 'After 2 cycles of intensive chemotherapy / Pre-alloHCT';
      case 'eot': return 'End of treatment (after consolidation / after alloHCT)';
      case 'eot_PML': return 'End of treatment (after consolidation)';
      case 'eot_intOther': return 'End of treatment after alloHCT';
      case 'follow_up': return 'Follow-up';
      case 'pre_hct': return 'Pre-alloHCT';
      default: return tp;
    }
  };

 const timePointData = getTimePointData();
 const rows = timePointData ? timePointData.rows : [];
 const referencedFootnotes = getReferencedFootnotes(rows, timePointData);

  // Function to convert number to superscript
  const toSuperscript = (num) => {
    const superscriptMap = {'0':'⁰', '1':'¹', '2':'²', '3':'³', '4':'⁴', '5':'⁵', '6':'⁶', '7':'⁷', '8':'⁸', '9':'⁹'};
    return num.toString().split('').map(digit => superscriptMap[digit] || digit).join('');
  };

  // Function to render a table for given table and timepoint
  const renderMRDTable = (tableKey, timePoint, showDefinitions = true) => {
    const table = tables[tableKey];
    if (!table || !table.timePoints[timePoint]) {
      return null;
    }
    
    const timePointData = table.timePoints[timePoint];
    const rows = timePointData.rows;
    
    // Special handling for NPM1 follow-up to separate PB and BM
    if (tableKey === 'NPM1' && timePoint === 'follow_up') {
      const pbRows = rows.filter(row => row.tissue === 'PB');
      const bmRows = rows.filter(row => row.tissue === 'BM');
      
      return (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-2 text-red-800"><GeneText>{table.title}</GeneText></h2>
          
          {/* Table Header/Subtitle with Time Point, Assay, and Tissue */}
          <div className="mb-4">
            <div className="text-md font-medium text-gray-700">
              <p><span className="font-semibold">Timepoint:</span> {getTimePointLabel(timePoint)}</p>
              <p><span className="font-semibold">Recommended assay:</span> {timePointData.assay}</p>
              <p><span className="font-semibold">Recommended tissue:</span> {timePointData.tissue}</p>
              {timePoint === 'follow_up' && timePointData.FU_duration && (
                <p><span className="font-semibold">Monitoring frequency:</span> {timePointData.FU_duration}</p>
              )}
            </div>
          </div>
          
          {/* PB Table */}
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2 text-gray-700">Peripheral blood (PB)</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                      <GeneText>mutNPM1/ABL1 COPIES (%)</GeneText>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantitative MRD burden
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Qualitative MRD response
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pbRows.map((row, rowIndex) => (
                    <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {row.threshold.split('').map((char, index) => {
                          if (char === ',') {
                            return <sup key={index}>,</sup>;
                          }
                          return char;
                        })}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {row.definition}
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${
                        row.response === 'Optimal' ? 'text-green-600' :
                        row.response === 'Warning' ? 'text-yellow-600' :
                        row.response === 'Warning³' ? 'text-yellow-600' :
                        row.response.includes('MRD relapse') ? 'text-red-800 font-bold' :
                        'text-gray-500'
                      }`}>
                        {row.response}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* BM Table */}
          <div>
            <h3 className="text-lg font-medium mb-2 text-gray-700">Bone marrow (BM)</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                      <GeneText>mutNPM1/ABL1 COPIES (%)</GeneText>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantitative MRD burden
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Qualitative MRD response
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bmRows.map((row, rowIndex) => (
                    <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {row.threshold.split('').map((char, index) => {
                          if (char === ',') {
                            return <sup key={index}>,</sup>;
                          }
                          return char;
                        })}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {row.definition}
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${
                        row.response === 'Optimal' ? 'text-green-600' :
                        row.response === 'Warning' ? 'text-yellow-600' :
                        row.response === 'Warning³' ? 'text-yellow-600' :
                        row.response.includes('MRD relapse') ? 'text-red-800 font-bold' :
                        'text-gray-500'
                      }`}>
                        {row.response}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Footnotes */}
          {(() => {
            const allFootnoteRefs = [...pbRows, ...bmRows];
            const referencedFootnotes = getReferencedFootnotes(allFootnoteRefs, timePointData);
            return referencedFootnotes.length > 0 ? (
              <div className="mt-4 text-xs text-gray-600 space-y-1">
                {referencedFootnotes.map(num => {
                  if (table.footnotes[num]) {
                    return <p key={num}>
                      {toSuperscript(num)}<GeneText>{table.footnotes[num]}</GeneText>
                    </p>
                  }
                  return null;
                })}
              </div>
            ) : null;
          })()}
          
          {/* Definitions and Abbreviations - only show if showDefinitions is true */}
          {showDefinitions && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg text-xs text-gray-700 space-y-3">
              <div>
                <p className="font-semibold">Qualitative MRD response:</p>
                <p>Classifies the MRD result into one of three categories based on its association with expected cal outcomes: (1) Optimal: Indicates low relapse risk; no change to the management is required. (2) Warning: MRD-LL with uncertain impact on relapse risk; close monitoring advised. (3) High risk of treatment failure: Associated with a high risk of relapse; modification of therapy should be considered.</p>
              </div>
              <div>
                <p className="font-semibold">Abbreviations:</p>
                <p>alloHCT, allogenic hematopoietic cell transplantation; BM, bone marrow; DfN, different from normal; EOT, end of treatment; LAIP, leukemia associated immunophenotype; LOD, limit of detection; MRD, measurable residual disease; MFC, multi parameter flow cytometry; NGS, next generation sequencing; PB, peripheral blood; qPCR, quantitative PCR; RT-qPCR, real time quantitative PCR; UHS-NGS, ultrahigh-sensitivity NGS; VAF, variant allele frequency</p>
              </div>
            </div>
          )}
        </div>
      );
    }
    
    // Regular table rendering for all other cases
    const referencedFootnotes = getReferencedFootnotes(rows, timePointData);
    
    return (
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-2 text-red-800"><GeneText>{table.title}</GeneText></h2>
        
        {/* Table Header/Subtitle with Time Point, Assay, and Tissue */}
        <div className="mb-4">
          <div className="text-md font-medium text-gray-700">
            <p><span className="font-semibold">Timepoint:</span> {getTimePointLabel(timePoint)}</p>
            <p><span className="font-semibold">Recommended assay:</span> {timePointData.assay}</p>
            <p><span className="font-semibold">Recommended tissue:</span> {timePointData.tissue}</p>
            {timePoint === 'follow_up' && timePointData.FU_duration && (
              <p><span className="font-semibold">Monitoring frequency:</span> {timePointData.FU_duration}</p>
            )}
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                  <GeneText>
                    {table.title.includes('NPM1') ? 'mutNPM1/ABL1 COPIES (%)' :
                     table.title.includes('CBF') ? 'CBF-MUTANT/ABL1 COPIES (%)' :
                     table.title.includes('PML') ? 'PML::RARA/ABL1 COPIES (%)' :
                     table.title.includes('UHS-NGS') ? 'LOD MEASURED AS VAF (%)' :
                     'MRD % (LAIP+ OR DfN+ BLASTS/CD45-EXPRESSING CELLS)'}
                  </GeneText>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantitative MRD burden
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Qualitative MRD response
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.length > 0 ? (
                rows.map((row, rowIndex) => (
                  <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.threshold.split('').map((char, index) => {
                        if (char === ',') {
                          return <sup key={index}>,</sup>;
                        }
                        return char;
                      })}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {row.definition}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${
                      row.response === 'Optimal' ? 'text-green-600' :
                      row.response === 'Warning' ? 'text-yellow-600' :
                      row.response === 'Warning³' ? 'text-yellow-600' :
                      row.response.includes('High risk') || row.response.includes('High risk of treatment failure') ? 'text-red-600' :
                      row.response.includes('MRD relapse') || row.response.includes('Molecular relapse') ? 'text-red-800 font-bold' :
                      row.response === '-' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {row.response}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="px-4 py-3 text-sm text-gray-500 text-center">
                    No specific recommendations available for this time point.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Footnotes - only showing referenced ones */}
        {referencedFootnotes.length > 0 && (
          <div className="mt-4 text-xs text-gray-600 space-y-1">
            {referencedFootnotes.map(num => {
              if (table.footnotes[num]) {
                return <p key={num}>
                  {toSuperscript(num)}<GeneText>{table.footnotes[num]}</GeneText>
                </p>
              }
              return null;
            })}
          </div>
        )}
        
        {/* Definitions and Abbreviations - only show if showDefinitions is true */}
        {showDefinitions && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg text-xs text-gray-700 space-y-3">
            <div>
              <p className="font-semibold">Qualitative MRD response:</p>
              <p>Classifies the MRD result into one of three categories based on its association with expected cal outcomes: (1) Optimal: Indicates low relapse risk; no change to the management is required. (2) Warning: MRD-LL with uncertain impact on relapse risk; close monitoring advised. (3) High risk of treatment failure: Associated with a high risk of relapse; modification of therapy should be considered.</p>
            </div>
            <div>
              <p className="font-semibold">Abbreviations:</p>
              <p>alloHCT, allogenic hematopoietic cell transplantation; BM, bone marrow; DfN, different from normal; EOT, end of treatment; LAIP, leukemia associated immunophenotype; LOD, limit of detection; MRD, measurable residual disease; MFC, multi parameter flow cytometry; NGS, next generation sequencing; PB, peripheral blood; qPCR, quantitative PCR; RT-qPCR, real time quantitative PCR; UHS-NGS, ultrahigh-sensitivity NGS; VAF, variant allele frequency</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Function to render multiple tables for a subtype timepoint
  const renderSubtypeRecommendations = () => {
    if (!subtype || !timePoint) return null;
    
    // Special case for KMT2A rearrangements
    if (subtype === 'KMT2A_MLLT3' || subtype === 'Fusion_KMT2A') {
      return (
        <div>
          {/* Header text for KMT2A */}
          <div className="mb-4 p-3 bg-gray-100 rounded-lg text-gray-700">
            <p className="font-semibold text-xl text-red-800 mb-2">qPCR if available or MFC</p>
            <p className="text-sm">
              <GeneText>Assays for KMT2A-rearranged AML MRD testing have been developed, </GeneText>
              but the role of clinical intervention in the setting of fusion-gene persistence in remission 
              at specific thresholds and treatment timepoints remains to be established <strong>(Recommendation C6)</strong>.
            </p>
          </div>
          
          {/* MFC recommendations table - show definitions since it's the only table */}
          {renderMRDTable('MFC', timePoint, true)}
        </div>
      );
    }
    
    // Get tables for this subtype
    const tableKeys = subtypeToTable[subtype];
    if (Array.isArray(tableKeys)) {
      // Multiple tables for this subtype
      return (
        <div>
          {tableKeys.map((tableKey, index) => {
            if (tables[tableKey] && tables[tableKey].timePoints && tables[tableKey].timePoints[timePoint]) {
              // Only show definitions for the last table
              const showDefinitions = index === tableKeys.length - 1;
              return renderMRDTable(tableKey, timePoint, showDefinitions);
            }
            return null;
          })}
        </div>
      );
    } else if (tableKeys) {
      // Single table for this subtype - show definitions
      return renderMRDTable(tableKeys, timePoint, true);
    }
    
    return null;
  };

  return (
    <div 
  className="min-h-screen bg-gray-200"
  style={{
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
  }}
>
    <div className="max-w-4xl mx-auto p-4">
<header className="mb-8 text-center">
  {/* Logo row - above the main header */}
<div className="flex justify-between items-end mb-3 px-2">
    <img
      src={Logo1}
      alt="Logo 1"
      className="h-11 sm:h-12 md:h-16 lg:h-20 w-auto"
    />
    <img
      src={Logo2}
      alt="Logo 2"
      className="h-7 sm:h-9 md:h-12 lg:h-14 w-auto max-w-[120px] sm:max-w-[150px] md:max-w-[200px] lg:max-w-[250px]"
    />
  </div>
  
  <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
    <h1 className="text-2xl font-bold text-red-900 mb-3 tracking-tight">Clinician's Guide</h1>
    <div className="h-1 w-32 bg-gradient-to-r from-red-600 to-red-800 mx-auto mb-4 rounded-full"></div>
    <p className="text-lg text-gray-700 max-w-2xl mx-auto">For the European LeukemiaNet (ELN) 2025 Measurable Residual Disease (MRD) Guidelines in Acute Myeloid Leukemia (AML)</p>
  </div>
</header>
  
      {/* New Collapsible Introduction Section */}
      <div className="bg-white rounded-lg shadow-md mb-6">
        <button
          onClick={() => setIntroExpanded(!introExpanded)}
          className="w-full px-6 py-4 flex items-center justify-between bg-gray-100 hover:bg-gray-200 transition-colors rounded-t-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          <h2 className="text-xl font-semibold text-gray-800">Introduction</h2>
          <svg 
            className={`w-6 h-6 text-gray-700 transform transition-transform ${introExpanded ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        <div 
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
    introExpanded ? 'opacity-100' : 'max-h-0 opacity-0'
          }`}
          style={introExpanded ? {} : {}}
              >
          <div className="px-6 py-4 text-gray-700 space-y-4">
            <p>
              The 2025 Update of the ELN MRD consensus guidelines for AML serve as a comprehensive guide for laboratory standardization and clinical implementation of MRD in AML. It discusses the optimal assays and analysis strategies per molecular AML subtype and provides recommendations on selecting timepoints, tissue type, and cutoff for each measurement.
            </p>
            
            <p>
              To facilitate rapid uptake and optimal use of the 2025 recommendations, we developed an interactive web application which provides a specific summary per AML subtype. Navigate through the website to find:
            </p>
            
            <ul className="list-disc list-inside space-y-2 pl-4">
              <li>Choice of MRD assay and analysis strategy for each molecular AML subtype</li>
              <li>Guidance on when to measure MRD during and after treatment</li>
              <li>Appropriate tissue types, analytical methods, and clinically validated cutoff values</li>
              <li>Interpretation of MRD burden based on qualitative response categories (optimal, warning, or high risk of treatment failure)</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">Select patient characteristics to view recommendations</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* AML Type Selection */}
          <div>
            <label htmlFor="amlType" className="block text-lg font-medium text-gray-700 mb-1">ELN 2022 Risk Category</label>
            <select
              id="amlType"
              value={amlType}
              onChange={handleAmlTypeChange}
              className="block w-full p-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
            >
              <option value="">Select Risk Category</option>
              <option value="favorable">Favorable</option>
              <option value="intermediate">Intermediate</option>
              <option value="adverse">Adverse</option>
            </select>
          </div>
          
          {/* Subtype Selection */}
          <div>
            <label htmlFor="subtype" className="block text-lg font-medium text-gray-700 mb-1">Molecular Subgroup</label>
            <select
              id="subtype"
              value={subtype}
              onChange={handleSubtypeChange}
              disabled={!amlType}
              className="block w-full p-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100"
            >
              <option value="">Select Subgroup</option>
              {amlType && subtypeOptions[amlType] ? subtypeOptions[amlType].map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              )) : null}
            </select>
          </div>
          
          {/* Time Point Selection */}
          <div>
            <label htmlFor="timePoint" className="block text-lg font-medium text-gray-700 mb-1">Assessment Timepoint</label>
            <select
              id="timePoint"
              value={timePoint}
              onChange={(e) => setTimePoint(e.target.value)}
              disabled={!subtype}
              className="block w-full p-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100"
            >
              <option value="">Select Time Point</option>
              {subtype && subtypeTimePoints[subtype] ? subtypeTimePoints[subtype].map(tp => (
                <option key={tp} value={tp}>{timePointOptions[tp].label}</option>
              )) : null}
            </select>
          </div>
        </div>
      </div>

      {/* Display MRD Assessment Tables */}
      {subtype && timePoint ? (
        <div className="bg-white p-6 rounded-lg shadow-md">
          {/* Display info text for specific subtypes */}
          {subtype === 'FLT3ITD_NPM1wt' && (
            <div className="mb-4 p-3 bg-gray-100 rounded-lg text-gray-700">
              <p className="font-semibold text-xl text-red-800"><GeneText>For FLT3-ITD and NPM1wt:</GeneText></p>
              <p className="text-sm"><GeneText>FLT3-ITD UHS-NGS if available; otherwise MFC</GeneText></p>
            </div>
          )}
          
          {subtype === 'FLT3ITD_NPM1mut' && (
            <div className="mb-4 p-3 bg-gray-100 rounded-lg text-gray-700">
              <p className="font-semibold text-xl text-red-800"><GeneText>For FLT3-ITD and mutNPM1:</GeneText></p>
              <p className="text-sm"><GeneText>qPCR for mutNPM1 and, if available, FLT3-ITD UHS-NGS</GeneText></p>
            </div>
          )}
          
          {subtype === 'Other_int' && (
            <div className="mb-4 p-3 bg-gray-100 rounded-lg text-gray-700">
              <p className="font-semibold text-xl text-red-800">MFC (if LAIP/DfN available) or UHS-NGS after alloHCT (if gene mutation available):</p>
              <p className="text-sm">See <strong>Recommendations C12</strong>: The prognostic impact of UHS NGS for other mutations than FLT3-ITD has been demonstrated before and after chemotherapy consolidation/alloHCT and during follow-up.
              The cutoffs, time points and tissues should be further validated gene by gene following the framework in Supplemental Table S7.</p>
              <p className="text-sm">See <strong>Recommendations C13</strong>: Mutations in genes associated with germline predisposition or age-related clonal hematopoiesis are not prognostic 
              at the time of remission assessment after intensive chemotherapy and before alloHCT and should not be taken into account at these time points. </p>
            </div>
          )}
          
          {subtype === 'Other_adv' && (
            <div className="mb-4 p-3 bg-gray-100 rounded-lg text-gray-700">
              <p className="font-semibold text-xl text-red-800">MFC (if LAIP/DfN available) or UHS-NGS after alloHCT (if gene mutation available):</p>
              <p className="text-sm">See <strong>Recommendations C12</strong>: The prognostic impact of UHS NGS for other mutations than FLT3-ITD has been demonstrated before and after chemotherapy consolidation/alloHCT and during follow-up.
              The cutoffs, time points and tissues should be further validated gene by gene following the framework in Supplemental Table S7.</p>
              <p className="text-sm">See <strong>Recommendations C13</strong>: Mutations in genes associated with germline predisposition or age-related clonal hematopoiesis are not prognostic 
              at the time of remission assessment after intensive chemotherapy and before alloHCT and should not be taken into account at these time points. </p>
            </div>
          )}
          
          {subtype === 'CEBPA_bZIP' && (
            <div className="mb-4 p-3 bg-gray-100 rounded-lg text-gray-700">
              <p className="font-semibold text-xl text-red-800">For CEBPA bZIP in-frame:</p>
              <p className="text-sm">There is some evidence that MFC-MRD from BM is prognostic in CEBPA mutated AML with biallelic mutations after two cycles of intensive chemotherapy. 
              Future research should also evaluate the prognostic effect of MFC-MRD in this subgroup at end of treatment and during follow-up <strong>(Recommendation B13)</strong>.</p>
            </div>
          )}
          
          {/* Render tables */}
          {renderSubtypeRecommendations()}
        </div>
      ) : null}
      
      {subtype === 'KMT2A_MLLT3' && !timePoint && (
        <div className="mt-4 text-sm text-gray-600">
          <p>¹ AML specific fusion genes as listed in Döhner et al. 2022 can be used if a validated assay is available.</p>
        </div>
      )}
      
    <footer className="mt-8 text-center text-sm text-gray-500">
  <p>
    This website by the ELN-DAVID summarizes the 2025 update of the ELN MRD consensus guidelines for AML (
    <a 
      href="https://doi.org/10.1182/blood.2025031480" 
      target="_blank" 
      rel="noopener noreferrer"
      className="text-gray-500 hover:text-gray-800"
    >
      doi:10.1182/blood.2025031480
    </a>
    ) and does not represent a medical device.
  </p>
  <p className="mt-2">Corresponding author: Prof. Dr. Michael Heuser (Michael.Heuser@uk-halle.de)</p>
  <p className="mt-2">App programmed by Lukas H. Haaksma</p>
</footer>
      <Analytics />
  </div>
</div>
);
}

export default App;
